// Audit-driven addition #4: UpdateNotice modal — polls /api/version every
// 90s. When BUILD_SHA differs from initial load → show blocking modal
// "A new version is available. Reload?". Pairs with lazyWithRetry's
// stale-chunk reload-once-per-session.

import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 90_000;

export function UpdateNotice() {
  const [initialSha, setInitialSha] = useState<string | null>(null);
  const [latestSha, setLatestSha] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const v = await api.version();
        if (cancelled) return;
        setLatestSha(v.build_sha);
        if (initialSha === null) setInitialSha(v.build_sha);
      } catch {
        /* network blip — ignore */
      }
    };
    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // initialSha intentionally not in deps — we want it set once.
  }, [initialSha]);

  if (initialSha === null || latestSha === null) return null;
  if (initialSha === latestSha) return null;
  // Don't pop the modal for "dev" → "dev"; only when SHAs are real and differ.
  if (initialSha === "dev" || latestSha === "dev") {
    if (initialSha === latestSha) return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-bg-card rounded-lg shadow-lg max-w-md w-full p-6">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCw size={18} className="text-accent" />
          <h2 className="font-semibold text-text-primary">
            A new version is available
          </h2>
        </div>
        <p className="text-sm text-text-muted mb-4">
          We've shipped an update. Reload to get the latest changes.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => window.location.reload()}
            className="text-sm px-4 py-2 bg-accent text-text-white rounded hover:bg-accent-hover"
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
